import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from '../services/axiosInstance';

/**
 * Custom hook for fetching and managing KPI data with Stale-While-Revalidate (SWR).
 * Integrates with the backend's multi-layer cache for sub-200ms latency.
 */
export const useKPI = (page = 1, pageSize = 10) => {
    const queryClient = useQueryClient();

    // Fetch KPIs
    const { data, isLoading, isError, error, refetch } = useQuery({
        queryKey: ['kpis', page, pageSize],
        queryFn: async () => {
            try {
                const response = await axios.get(`/api/provider/kpi?page=${page}&pageSize=${pageSize}`);
                return response.data;
            } catch (err) {
                console.error(`[useKPI] Error fetching KPIs (page ${page}):`, err);
                throw err;
            }
        },
        staleTime: 60000, // 1 minute stale time (L1 cache on frontend)
        gcTime: 300000, // 5 minutes cache time
    });

    // Re-calculate KPIs (Mutate)
    const mutation = useMutation({
        mutationFn: async () => {
            // Trigger backend re-analysis
            const response = await axios.post('/api/tasks/recompute-kpi');
            return response.data;
        },
        onSuccess: () => {
            // Invalidate and refetch
            queryClient.invalidateQueries({ queryKey: ['kpis'] });
        },
        onError: (err) => {
            console.error('[useKPI] Error triggering recompute:', err);
        }
    });

    return {
        kpis: data?.kpis || {},
        isLoading,
        isError: isError || mutation.isError,
        error: error || mutation.error,
        recompute: mutation.mutate,
        isComputing: mutation.isPending,
        refetch
    };
};

/**
 * Hook for Data Lake Quality metrics with Spark-enhanced backend logic.
 */
export const useDataLakeQuality = () => {
    return useQuery({
        queryKey: ['data-lake-quality'],
        queryFn: async () => {
            try {
                const response = await axios.get('/api/data-quality/data-lake');
                return response.data;
            } catch (err) {
                console.error('[useDataLakeQuality] Error fetching stats:', err);
                throw err;
            }
        },
        staleTime: 30000, // 30 seconds (re-fetch more quickly after upload)
    });
};

/**
 * Hook for Data Warehouse Quality metrics.
 */
export const useDataWarehouseQuality = () => {
    return useQuery({
        queryKey: ['data-warehouse-quality'],
        queryFn: async () => {
            try {
                const response = await axios.get('/api/data-quality/data-warehouse');
                return response.data;
            } catch (err) {
                console.error('[useDataWarehouseQuality] Error fetching stats:', err);
                throw err;
            }
        },
        staleTime: 30000,
    });
};
